import { Channel, OnlineUser, PresenceManual, PresenceStatus } from '../types';

/**
 * DM 상대의 접속 정보.
 * dmPartnerId(사번) 매칭을 우선하고, 없으면 이름 매칭으로 폴백한다(구버전 서버 호환).
 * 검색·조직도에서 방금 연 DM은 activeChannel에 dmPartnerId가 없을 수 있으므로,
 * 호출자는 channels 배열에서 찾은 완전한 채널 객체를 넘기는 편이 안전하다.
 */
export function findDmPartner(channel: Pick<Channel, 'type' | 'name' | 'dmPartnerId'>, onlineUsers: OnlineUser[]): OnlineUser | undefined {
  if (channel.type !== 'dm') return undefined;
  if (channel.dmPartnerId) {
    return onlineUsers.find(u => String(u.userId) === String(channel.dmPartnerId));
  }
  if (channel.name) {
    return onlineUsers.find(u => u.userName === channel.name);
  }
  return undefined;
}

export const STATUS_LABEL: Record<PresenceStatus, string> = {
  online: '온라인',
  away: '자리 비움',
  busy: '다른 용무 중',
  offline: '오프라인',
};

/**
 * 접속 상태 점 색상.
 * ⚠ 클래스명은 Tailwind JIT가 소스에서 스캔해야 하므로 반드시 리터럴로 둔다(문자열 조립 금지).
 * red-500은 디자인 토큰 규칙상 '다른 용무 중'에만 남겨 둔 예외다.
 * 오프라인은 slate-400 — slate-300은 흰 바탕에서 묻혀 "표시가 없다"는 신고가 있었고
 * (ChannelListItem의 상태 아이콘 주석), slate-500은 오프라인치고 너무 진하다.
 * ⚠ 이 맵을 직접 읽지 말고 PresenceDot을 쓸 것 — 예전에 6곳이 각자 복제해 오프라인 색과
 *   한글 라벨이 화면마다 달랐다.
 */
export const STATUS_DOT_CLASS: Record<PresenceStatus, string> = {
  online: 'bg-green-500',
  away: 'bg-yellow-500',
  busy: 'bg-red-500',
  offline: 'bg-slate-400',
};

/**
 * 수동 선택 3지선다. 'auto'는 "자동 판정으로 복귀"이고 강제 온라인이 아니다 —
 * 서버가 'online'을 수동값으로 받지 않는다(MANUAL_STATUS_VALUES 주석).
 * 라디오 목록(PresenceStatusSetting)과 rail 팝오버 메뉴가 이 배열 하나를 공유한다.
 */
export const PRESENCE_CHOICES: ReadonlyArray<{
  value: 'auto' | 'away' | 'busy';
  /** 점 색·비활성 표시에 쓰는 대표 상태 */
  dot: PresenceStatus;
  label: string;
  description: string;
}> = [
  {
    value: 'auto',
    dot: 'online',
    label: '접속중 (자동)',
    description: '자리를 비우거나 화면을 잠그면 자동으로 자리 비움이 됩니다',
  },
  {
    value: 'away',
    dot: 'away',
    label: '자리 비움',
    description: 'PC를 쓰는 중이어도 자리 비움으로 보입니다',
  },
  {
    value: 'busy',
    dot: 'busy',
    label: '다른 용무 중',
    description: '알림은 그대로 옵니다. 표시만 바뀝니다',
  },
];

/** 수동값(null=자동)을 선택 UI의 value로. 서버가 보내는 manual과 라디오를 잇는 유일한 변환 */
export const manualToChoice = (manual: PresenceManual): 'auto' | 'away' | 'busy' =>
  manual === 'away' || manual === 'busy' ? manual : 'auto';
